import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Calendar, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { publicApi } from "@/lib/api";

const CATEGORIES = ["All", "Travel Guide", "Destination", "Tips", "News"];

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (selectedCategory === "All") {
      setFilteredPosts(posts);
    } else {
      setFilteredPosts(posts.filter((p) => p.category === selectedCategory));
    }
  }, [selectedCategory, posts]);

  const fetchPosts = async () => {
    try {
      const response = await publicApi.get("/blog");
      setPosts(response.data);
      setFilteredPosts(response.data);
    } catch (error) {
      console.error("Error fetching blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div data-testid="blog-page" className="grain-texture min-h-screen">
      <SEO
        title="Andaman Travel Guide & Tips - Blog"
        description="Expert Andaman travel guides, tips, honeymoon ideas, best beaches, scuba diving spots and destination stories from Andaman Avalon."
        path="/blog"
      />
      <Navbar />

      {/* Hero Header */}
      <section className="pt-32 pb-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">TRAVEL GUIDES & TIPS</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-4 mb-6 text-white">
            Andaman Journal
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Insider tips, complete guides, and stories from the world's most beautiful islands
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-muted sticky top-20 z-40 border-b border-border" data-testid="blog-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {CATEGORIES.map((category) => (
              <Button
                key={category}
                data-testid={`blog-filter-${category.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category ? "bg-primary" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">No articles in this category yet.</p>
            </div>
          ) : (
            <>
              {/* Featured Post (First) */}
              {filteredPosts[0] && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-16"
                  data-testid="featured-post"
                >
                  <Link to={`/blog/${filteredPosts[0].slug}`} className="grid grid-cols-1 lg:grid-cols-2 gap-8 group">
                    <div className="relative h-80 lg:h-full rounded-xl overflow-hidden">
                      <img
                        src={filteredPosts[0].image_url}
                        alt={filteredPosts[0].title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase">
                        Featured
                      </div>
                    </div>
                    <div className="flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs bg-muted text-primary px-3 py-1 rounded-full font-semibold uppercase">
                          {filteredPosts[0].category}
                        </span>
                        <span className="flex items-center text-sm text-muted-foreground">
                          <Clock size={14} className="mr-1" />
                          {filteredPosts[0].read_time} min read
                        </span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif mb-4 group-hover:text-secondary transition-colors">
                        {filteredPosts[0].title}
                      </h2>
                      <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                        {filteredPosts[0].excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <User size={14} className="mr-1" />
                          {filteredPosts[0].author}
                          <span className="mx-2">·</span>
                          <Calendar size={14} className="mr-1" />
                          {formatDate(filteredPosts[0].published_at)}
                        </div>
                        <span className="text-secondary font-medium flex items-center group-hover:translate-x-1 transition-transform">
                          Read Article <ArrowRight className="ml-1" size={16} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* Other Posts */}
              {filteredPosts.length > 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.slice(1).map((post, index) => (
                    <motion.div
                      key={post.id}
                      data-testid={`blog-post-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className="package-card bg-white rounded-lg overflow-hidden border border-border/50 shadow-sm block group"
                      >
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute top-3 left-3 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase">
                            {post.category}
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-3 text-sm text-muted-foreground">
                            <span className="flex items-center">
                              <Clock size={14} className="mr-1" />
                              {post.read_time} min
                            </span>
                            <span>·</span>
                            <span>{formatDate(post.published_at)}</span>
                          </div>
                          <h3 className="text-xl font-serif mb-3 group-hover:text-secondary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-muted-foreground line-clamp-3">
                            {post.excerpt}
                          </p>
                          <div className="mt-4 flex items-center text-secondary font-medium">
                            Read More <ArrowRight className="ml-1" size={16} />
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
